/**
 * Copyright (c) Simam Digital. Built on Project Flowerbed
 * (Copyright (c) Meta Platforms, Inc. and affiliates).
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {
	LeaderboardComponent,
	MultiplayerStateComponent,
} from '../../components/MultiplayerComponents';
import {
	PlantedComponent,
	isPlantMature,
} from '../../components/PlantingComponents';
import {
	PlayerWalletComponent,
	RegionComponent,
} from '../../components/EconomyComponents';
import {
	RARITY_COLOR,
	SEED_FAMILIES,
	getSeedInfo,
	seedFamilyOf,
} from '../../SeedCatalog';

import { GameStateComponent } from '../../components/GameStateComponent';
import { GardenHudOverlay } from '../../lib/objects/GardenHudOverlay';
import { SavableObject } from '../../components/SaveDataComponents';
import { SeedboxComponent } from '../../components/SeedboxComponents';
import { System } from 'ecsy';
import { THREEGlobalComponent } from '../../components/THREEGlobalComponent';
import { getOnlyEntity } from '../../utils/entityUtils';

// How long to wait after entering before showing the welcome-back card, so the
// local save + cloud restore have populated the garden first.
const WELCOME_DELAY_MS = 4000;
// Refresh the "last seen" stamp this often while playing, so "while you were
// away" measures from roughly when the player actually left.
const LAST_SEEN_HEARTBEAT_MS = 20000;
const LAST_SEEN_KEY = 'gxs_last_seen_at';

function readLastSeen() {
	try {
		const v = window.localStorage.getItem(LAST_SEEN_KEY);
		return v ? parseInt(v, 10) : null;
	} catch (e) {
		return null;
	}
}

function writeLastSeen(t) {
	try {
		window.localStorage.setItem(LAST_SEEN_KEY, String(t));
	} catch (e) {
		// localStorage unavailable (private mode etc.) — welcome-back just won't
		// have a baseline; harmless.
	}
}

const MODES = GameStateComponent.INTERACTION_MODES;
const MODE_FOR_NAME = {
	plant: MODES.PLANTING,
	water: MODES.WATERING,
	harvest: MODES.PICKING,
	explore: MODES.DEFAULT,
};

/**
 * Drives the flatscreen/mobile grow-a-garden HUD (GardenHudOverlay) from the
 * ECS world: shows it while a (flatscreen-emulated or VR) session is running,
 * mirrors the coin balance, and lets the player switch interaction modes and
 * pick seeds — the mode-switch path that VR gets from the selection wheel but
 * flatscreen otherwise lacks.
 */
export class GardenHudSystem extends System {
	init() {
		this._overlay = new GardenHudOverlay();
		this._visible = false;
		this._seedHash = '';
		this._welcomeChecked = false;
		this._presentingSinceMs = 0;
		this._lastHeartbeatMs = 0;

		// The welcome-back card's "Harvest now" button drops the player straight
		// into harvest mode.
		this._overlay.onHarvest = () => {
			const gameState = this._getGameState();
			if (gameState) gameState.setInteractionMode(MODES.PICKING);
		};

		// Best-effort: stamp "last seen" on the way out so the next visit measures
		// "while you were away" from roughly when the player left.
		window.addEventListener('beforeunload', () => writeLastSeen(Date.now()));

		this._overlay.onSetMode = (modeName) => {
			const gameState = this._getGameState();
			if (!gameState) return;
			const mode = MODE_FOR_NAME[modeName];
			if (mode === undefined) return;
			// Toggle: tapping the already-active mode returns to auto (DEFAULT),
			// so Water can be switched off without a dedicated Explore button.
			if (gameState.interactionMode === mode) {
				gameState.setInteractionMode(MODES.DEFAULT);
			} else {
				gameState.setInteractionMode(mode);
			}
		};

		this._overlay.onSelectSeed = (groupIndex) => {
			const seedbox = this._getSeedbox();
			const gameState = this._getGameState();
			if (!seedbox || !gameState) return;
			seedbox.currentPlantIndex = groupIndex;
			gameState.setInteractionMode(MODES.PLANTING);
			this._overlay.closeShop();
			this._seedHash = ''; // force shop refresh to update selection
		};
	}

	_getGameState() {
		const entity = getOnlyEntity(this.queries.gameManager, false);
		return entity ? entity.getMutableComponent(GameStateComponent) : null;
	}

	_getSeedbox() {
		const entity = getOnlyEntity(this.queries.seedbox, false);
		return entity ? entity.getMutableComponent(SeedboxComponent) : null;
	}

	/** Display name of the currently selected seed, for the context hint. */
	_selectedSeedName() {
		const seedbox = this._getSeedbox();
		if (!seedbox) return '';
		const group = (seedbox.plantGroups || [])[seedbox.currentPlantIndex];
		return group ? getSeedInfo(group).displayName : '';
	}

	execute() {
		const gameManager = getOnlyEntity(this.queries.gameManager, false);
		if (!gameManager) return;
		const renderer = gameManager.getComponent(THREEGlobalComponent).renderer;
		const gameState = gameManager.getComponent(GameStateComponent);

		const presenting = renderer.xr.isPresenting;
		if (presenting !== this._visible) {
			this._visible = presenting;
			if (presenting) {
				this._overlay.show();
				this._presentingSinceMs = Date.now();
				this._lastHeartbeatMs = Date.now();
				this._welcomeChecked = false;
			} else {
				this._overlay.hide();
				writeLastSeen(Date.now());
			}
		}
		if (!presenting) return;

		const now = Date.now();
		// One-shot "while you were away" card, once the garden has had a moment to
		// load in from local save + cloud restore.
		if (
			!this._welcomeChecked &&
			now - this._presentingSinceMs > WELCOME_DELAY_MS
		) {
			this._welcomeChecked = true;
			this._checkWelcomeBack(now);
		}
		// Keep the "last seen" stamp fresh while the player is actually here.
		if (now - this._lastHeartbeatMs > LAST_SEEN_HEARTBEAT_MS) {
			this._lastHeartbeatMs = now;
			writeLastSeen(now);
		}

		const walletEntity = getOnlyEntity(this.queries.wallet, false);
		const wallet = walletEntity
			? walletEntity.getComponent(PlayerWalletComponent)
			: null;
		if (wallet) this._overlay.setCoins(wallet.coins);

		// Reflect the active interaction mode on the mode buttons.
		let activeName = 'explore';
		if (gameState.interactionMode === MODES.PLANTING) activeName = 'plant';
		else if (gameState.interactionMode === MODES.WATERING) activeName = 'water';
		else if (gameState.interactionMode === MODES.PICKING)
			activeName = 'harvest';
		this._overlay.setActiveMode(activeName);
		this._overlay.setContextHint(activeName, this._selectedSeedName());

		this._refreshSeeds(wallet);
		this._refreshLeaderboard();
	}

	_refreshLeaderboard() {
		const lbEntity = getOnlyEntity(this.queries.leaderboard, false);
		const mpEntity = getOnlyEntity(this.queries.multiplayer, false);
		const leaderboard = lbEntity
			? lbEntity.getComponent(LeaderboardComponent)
			: null;
		const multiplayer = mpEntity
			? mpEntity.getComponent(MultiplayerStateComponent)
			: null;

		const visitorCountries = leaderboard ? leaderboard.visitorCountries : {};
		const countries = Object.entries(visitorCountries || {})
			.map(([name, count]) => ({ name, count }))
			.sort((a, b) => b.count - a.count);

		let you = null;
		if (multiplayer) {
			you = {
				name: multiplayer.localPlayerName,
				country: multiplayer.localPlayerCountry,
			};
		}

		this._overlay.setLeaderboard({
			totalVisitors: leaderboard ? leaderboard.totalVisitors : 0,
			countries,
			you,
		});
	}

	/**
	 * Compute and (if there's anything to harvest) show the "while you were away"
	 * card. Own plants are `PlantedComponent + SavableObject` (foreign plants lack
	 * SavableObject). Ripeness and ripen-time both come from the wall-clock fields
	 * on PlantedComponent, so this reflects growth that happened while away.
	 */
	_checkWelcomeBack(now) {
		const lastSeen = readLastSeen();
		let readyCount = 0;
		let readyCoins = 0;
		let ripenedAway = 0;

		this.queries.ownPlants.results.forEach((entity) => {
			const planted = entity.getComponent(PlantedComponent);
			if (isPlantMature(planted)) {
				readyCount++;
				readyCoins += getSeedInfo(planted.plantType).sellValue;
			}
			const ripeAt =
				planted.plantedAt && planted.realGrowthDuration
					? planted.plantedAt + planted.realGrowthDuration
					: 0;
			if (lastSeen && ripeAt && ripeAt > lastSeen && ripeAt <= now) {
				ripenedAway++;
			}
		});

		// Baseline the stamp for subsequent heartbeats / the next visit.
		writeLastSeen(now);

		if (readyCount <= 0) return; // nothing waiting — skip the card.

		let title;
		let subtitle;
		if (ripenedAway > 0) {
			const s = ripenedAway === 1 ? '' : 's';
			title = `🌼 While you were away, ${ripenedAway} plant${s} ripened!`;
			subtitle = `${readyCount} ready to harvest · 🪙 ${readyCoins} coins waiting`;
		} else {
			const s = readyCount === 1 ? '' : 's';
			title = `🌼 ${readyCount} plant${s} ready to harvest`;
			subtitle = `🪙 ${readyCoins} coins waiting in your garden`;
		}
		this._overlay.showWelcomeBack({ title, subtitle });
	}

	_refreshSeeds(wallet) {
		const seedbox = this._getSeedbox();
		if (!seedbox || !wallet) return;
		const regionEntity = getOnlyEntity(this.queries.region, false);
		const region = regionEntity
			? regionEntity.getComponent(RegionComponent)
			: null;

		// Only rebuild the shop DOM when something it depends on changes.
		const hash = `${wallet.coins}|${seedbox.currentPlantIndex}|${
			(seedbox.plantGroups || []).length
		}`;
		if (hash === this._seedHash) return;
		this._seedHash = hash;

		const groups = seedbox.plantGroups || [];
		const familyIndex = {};
		groups.forEach((group, i) => {
			const family = seedFamilyOf(group);
			if (!(family in familyIndex)) familyIndex[family] = i;
		});

		const selectedGroup = groups[seedbox.currentPlantIndex];
		const selectedFamily = selectedGroup ? seedFamilyOf(selectedGroup) : '';

		const seeds = [];
		Object.keys(SEED_FAMILIES).forEach((family) => {
			if (!(family in familyIndex)) return;
			const info = SEED_FAMILIES[family];
			seeds.push({
				family,
				displayName: info.displayName,
				cost: info.cost,
				rarity: info.rarity,
				color: RARITY_COLOR[info.rarity],
				groupIndex: familyIndex[family],
				affordable: wallet.coins >= info.cost,
				signature: !!region && region.signatureSeed === family,
				selected: selectedFamily === family,
			});
		});
		this._overlay.setSeeds(seeds);
	}
}

GardenHudSystem.queries = {
	gameManager: {
		components: [GameStateComponent, THREEGlobalComponent],
	},
	seedbox: { components: [SeedboxComponent] },
	wallet: { components: [PlayerWalletComponent] },
	region: { components: [RegionComponent] },
	leaderboard: { components: [LeaderboardComponent] },
	multiplayer: { components: [MultiplayerStateComponent] },
	ownPlants: { components: [PlantedComponent, SavableObject] },
};
